import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CollectionsIcon from '@mui/icons-material/Collections'
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material'
import { Dispatch, FC, useCallback } from 'react'

import { AddMediaButton } from '../../../components/MediaGallery/AddMediaButton'
import MediaGallery, {
  MediaEntryType
} from '../../../components/MediaGallery/MediaGallery'
import { EntryWithRelated } from '../../../models/inspections'
import { InspectionMediaEntry } from '../../../sdk'

interface Props {
  media: InspectionMediaEntry[]
  promptId: string
  setEntry: Dispatch<React.SetStateAction<EntryWithRelated>>
  open: boolean
  setOpen: (open: boolean) => void
}

const EntryMediaButton: FC<Props> = ({
  media,
  promptId,
  setEntry,
  open,
  setOpen
}) => {
  const uploadCallback = useCallback(
    (newMediaEntry: InspectionMediaEntry) => {
      setEntry(
        (prev: EntryWithRelated) =>
          ({
            ...prev,
            prompts: [
              {
                ...prev.prompts[0],
                media: [...prev.prompts[0].media, newMediaEntry]
              }
            ]
          } as EntryWithRelated)
      )
    },
    [setEntry]
  )
  const updateCallback = useCallback(
    (
      oldMediaEntry: InspectionMediaEntry,
      newMediaEntry: InspectionMediaEntry
    ) => {
      setEntry(
        (prev: EntryWithRelated) =>
          ({
            ...prev,
            prompts: [
              {
                ...prev.prompts[0],
                media: [
                  ...prev.prompts[0].media.filter(
                    (m) => m.id !== oldMediaEntry.id!
                  ),
                  newMediaEntry
                ]
              }
            ]
          } as EntryWithRelated)
      )
      URL.revokeObjectURL(oldMediaEntry.mediaUrl!)
    },
    [setEntry]
  )

  const deleteCallback = useCallback(
    (mediaEntry: InspectionMediaEntry) => {
      setEntry((prev: EntryWithRelated) => ({
        ...prev,
        prompts: [
          {
            ...prev.prompts[0],
            media: [
              ...prev.prompts[0].media.filter((m) => m.id !== mediaEntry.id!)
            ]
          }
        ]
      }))
      URL.revokeObjectURL(mediaEntry.mediaUrl!)
    },
    [setEntry]
  )

  return (
    <>
      {media.length === 0 ? (
        <IconButton
          onClick={(event) => {
            event?.stopPropagation()
          }}
          color={'default'}
          component="label">
          <CameraAltIcon />
          <AddMediaButton
            mediaEntryType={MediaEntryType.inspectionPrompt}
            parentId={promptId}
            isMobileDevice
            uploadCallback={uploadCallback}
          />
        </IconButton>
      ) : (
        <IconButton
          onClick={(event) => {
            event?.stopPropagation()
            setOpen(true)
          }}
          color={'primary'}
          component="label">
          <Badge badgeContent={media.length} color="secondary">
            <CollectionsIcon />
          </Badge>
        </IconButton>
      )}
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          setOpen(false)
        }}>
        <DialogTitle>Media Gallery</DialogTitle>
        <DialogContent>
          <MediaGallery
            mediaEntryType={MediaEntryType.inspectionPrompt}
            media={media}
            parentId={promptId}
            uploadCallback={uploadCallback}
            updateCallback={updateCallback}
            deleteCallback={deleteCallback}
            isMobileDevice
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false)
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EntryMediaButton
